import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  TextField,
  CircularProgress,
  Chip,
  Avatar,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import ApiService from "../../services/api-service";
import PremiumPaymentHistory from "./PremiumPaymentHistory";
import "./Admin.scss";

export default function Premium() {
  const history = useHistory();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [stats, setStats] = useState({
    totalPremium: 0,
    percentageOfTotal: 0,
    totalUsers: 0
  });

  useEffect(() => {
    fetchAllPremiumUsers();
  }, []);

  const fetchAllPremiumUsers = async () => {
    setLoading(true);
    try {
      let allPremiumUsers = [];
      let pageNo = 1;
      let hasMore = true;
      
      console.log("Fetching ONLY premium users (hasPremium=true)...");
      
      // ONLY fetch premium users - no fallback
      while (hasMore) {
        console.log("Requesting premium users with params:", { pageNo, pageSize: 50, hasPremium: true });
        const response = await ApiService.ADMIN_GET_ALL_USERS({
          pageNo,
          pageSize: 50,
          hasPremium: true  // Request ONLY premium users
        });
        
        console.log(`Page ${pageNo}: Got ${response?.result?.response?.length || 0} premium users`);
        
        if (response?.result?.response?.length > 0) {
          allPremiumUsers = [...allPremiumUsers, ...response.result.response];
          
          // Check if we should continue
          if (response.result.response.length < 50) {
            hasMore = false;
          } else {
            pageNo++;
          }
        } else {
          hasMore = false;
        }
      }
      
      // Get total user count for percentage calculation (without filter)
      const totalUsersResponse = await ApiService.ADMIN_GET_ALL_USERS({
        pageNo: 1,
        pageSize: 1,  // Just need the total count
      });
      const allUsersCount = totalUsersResponse?.result?.totalCount || totalUsersResponse?.result?.count || 0;
      
      // Calculate stats
      setStats({
        totalPremium: allPremiumUsers.length,
        totalUsers: allUsersCount,
        percentageOfTotal: allUsersCount > 0 
          ? ((allPremiumUsers.length / allUsersCount) * 100).toFixed(1)
          : 0
      });
      
      console.log(`Total premium users found: ${allPremiumUsers.length}`);
      
      setUsers(allPremiumUsers);
      setFilteredUsers(allPremiumUsers);
    } catch (error) {
      console.error("Failed to fetch premium users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    
    const filtered = users.filter(user => {
      const name = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
      const email = (user.email || '').toLowerCase();
      const id = (user._id || '').toLowerCase();
      
      return name.includes(term) || email.includes(term) || id.includes(term);
    });
    
    setFilteredUsers(filtered);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box className="adminPortalContainer">
      <Typography className="adminHeader" variant="h4" gutterBottom>
        Premium Users
      </Typography>
      
      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: '#f59e0b', color: '#000' }}>
            <CardContent>
              <Typography variant="h3" component="div">
                {stats.totalPremium}
              </Typography>
              <Typography variant="h6">
                Premium Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: '#1f2937', color: '#fff' }}>
            <CardContent>
              <Typography variant="h3" component="div">
                {stats.percentageOfTotal}%
              </Typography>
              <Typography variant="h6">
                of Total Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: '#1f2937', color: '#fff' }}>
            <CardContent>
              <Typography variant="h3" component="div">
                {stats.totalUsers}
              </Typography>
              <Typography variant="h6">
                Total Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ backgroundColor: '#1f2937', color: '#fff' }}>
            <CardContent>
              <Typography variant="body1" sx={{ mb: 1 }}>Payment Methods</Typography>
              <Typography variant="body2">
                iOS: {users.filter(u => u.deviceType === 2).length}
              </Typography>
              <Typography variant="body2">
                Android: {users.filter(u => u.deviceType === 1).length}
              </Typography>
              <Typography variant="body2">
                Web/Other: {users.filter(u => !u.deviceType || (u.deviceType !== 1 && u.deviceType !== 2)).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and List */}
      <Box className="tabPanel">
        <TextField
          fullWidth
          className="textField"
          label="Search premium users by name, email, or ID"
          value={searchTerm}
          onChange={handleSearch}
          disabled={loading}
          sx={{ mb: 3 }}
        />
        
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress sx={{ color: '#f59e0b' }} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2, color: '#999' }}>
              Showing {filteredUsers.length} premium users
            </Typography>
            
            <List className="listRoot">
              {filteredUsers.map(user => (
                <ListItem
                  key={user._id}
                  className={
                    selectedUser && selectedUser._id === user._id
                      ? "selectedItem"
                      : "listItem"
                  }
                  onClick={() => setSelectedUser(user)}
                  selected={selectedUser && selectedUser._id === user._id}
                  sx={{
                    mb: 1,
                    borderRadius: 1,
                    border: '1px solid #333',
                    '&:hover': {
                      borderColor: '#f59e0b',
                      backgroundColor: 'rgba(245, 158, 11, 0.05)'
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" width="100%" gap={2}>
                    <Avatar
                      src={user.profileImg?.filePath}
                      alt={user.firstName}
                      sx={{ width: 50, height: 50 }}
                    >
                      {user.firstName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                    </Avatar>
                    
                    <Box flex={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {user.firstName} {user.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                    
                    <Box textAlign="right">
                      <Chip
                        label="PREMIUM"
                        size="small"
                        sx={{
                          backgroundColor: '#f59e0b',
                          color: '#000',
                          fontWeight: 'bold'
                        }}
                      />
                      {user.isVerified && (
                        <Chip
                          label="VERIFIED"
                          size="small"
                          sx={{
                            ml: 1,
                            backgroundColor: '#3b82f6',
                            color: '#fff'
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          </>
        )}
        
        {/* Selected User Details */}
        {selectedUser && (
          <Paper 
            elevation={3} 
            sx={{ 
              mt: 3, 
              p: 3, 
              backgroundColor: '#1f2937',
              border: '1px solid #f59e0b'
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ color: '#f59e0b' }}>
                User Details
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowPaymentHistory(true)}
                sx={{
                  borderColor: '#f59e0b',
                  color: '#f59e0b',
                  '&:hover': {
                    borderColor: '#d97706',
                    backgroundColor: 'rgba(245, 158, 11, 0.1)'
                  }
                }}
              >
                View Payment History
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedUser.firstName} {selectedUser.lastName}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedUser.email}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">User ID</Typography>
                <Typography variant="body1" gutterBottom sx={{ fontSize: '0.875rem' }}>
                  {selectedUser._id}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Subscription Status</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedUser.subscriptionStatus || 'Active'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">Subscription Fee</Typography>
                <Typography variant="body1" gutterBottom>
                  ${selectedUser.subscriptionFee || '0'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">Auto Renewal</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedUser.autoRenewal ? 'Enabled' : 'Disabled'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">Device Type</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedUser.deviceType === 1 ? 'Android' : selectedUser.deviceType === 2 ? 'iOS' : 'Web/Unknown'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Followers</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedUser.followers || 0}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">Account Balance</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedUser.accountBalance || 0} SIKI
                </Typography>
                
                <Typography variant="body2" color="text.secondary">Streaming Balance</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedUser.streamingBalance || 0}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">MoonPay Customer ID</Typography>
                <Typography variant="body1" gutterBottom sx={{ fontSize: '0.875rem' }}>
                  {selectedUser.moonPayCustomerId || 'Not set'}
                </Typography>
              </Grid>
              
              {/* Wallet Balances */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: '#f59e0b' }}>
                  Wallet Balances
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">SIKI Tokens</Typography>
                    <Typography variant="body1">
                      {selectedUser.wallet?.balance || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">HBAR</Typography>
                    <Typography variant="body1">
                      {selectedUser.wallet?.hbarBalance || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">MATIC</Typography>
                    <Typography variant="body1">
                      {selectedUser.wallet?.polygonBalance || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">NEAR</Typography>
                    <Typography variant="body1">
                      {selectedUser.wallet?.nearBalance || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        )}
        
        {/* Payment History Modal */}
        {showPaymentHistory && selectedUser && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
              p: 2
            }}
            onClick={() => setShowPaymentHistory(false)}
          >
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{ width: '90%', maxWidth: '1200px' }}
            >
              <PremiumPaymentHistory
                userId={selectedUser._id}
                onClose={() => setShowPaymentHistory(false)}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}